import React from 'react';
import type { PaginationMeta } from '@yawi/domain';
import { PAGE_SIZE_OPTIONS } from '@yawi/domain';

interface PaginationProps {
  meta: PaginationMeta | null;
  onPageChange: (page: number) => void;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  loading?: boolean;
}

export const Pagination: React.FC<PaginationProps> = ({
  meta,
  onPageChange,
  pageSize,
  onPageSizeChange,
  loading = false,
}) => {
  if (!meta || (meta.totalPages <= 1 && !onPageSizeChange)) return null;

  const { page, total, totalPages, hasNextPage, hasPreviousPage, limit } = meta;

  const startRecord = (page - 1) * limit + 1;
  const endRecord = Math.min(page * limit, total);

  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (page > 3) {
        pages.push('ellipsis');
      }

      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (page < totalPages - 2) {
        pages.push('ellipsis');
      }

      pages.push(totalPages);
    }

    return pages;
  };

  const buttonBaseClass = `
    inline-flex items-center justify-center w-9 h-9 text-sm font-medium rounded-lg
    transition-all focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-500
    disabled:opacity-50 disabled:cursor-not-allowed
  `;

  const pageButtonClass = (isActive: boolean) => `
    ${buttonBaseClass}
    ${
      isActive
        ? 'bg-primary-600 text-white hover:bg-primary-700'
        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
    }
  `;

  const navButtonClass = `
    ${buttonBaseClass}
    bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700
  `;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
      {/* Left side: Info text and page size selector */}
      <div className="flex items-center gap-4">
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Showing <span className="font-medium text-gray-800 dark:text-gray-200">{startRecord}</span> to{' '}
          <span className="font-medium text-gray-800 dark:text-gray-200">{endRecord}</span> of{' '}
          <span className="font-medium text-gray-800 dark:text-gray-200">{total}</span> records
        </div>

        {onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 dark:text-gray-400">Show:</span>
            <select
              value={pageSize || limit}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              disabled={loading}
              className="px-2 py-1 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20 disabled:opacity-50"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Pagination controls */}
      <div className="flex items-center gap-2">
        <button
          className={navButtonClass}
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPreviousPage || loading}
          aria-label="Previous page"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="hidden sm:flex items-center gap-1">
          {getPageNumbers().map((pageNum, index) =>
            pageNum === 'ellipsis' ? (
              <span key={`ellipsis-${index}`} className="px-2 text-gray-400 dark:text-gray-500">
                ...
              </span>
            ) : (
              <button
                key={pageNum}
                className={pageButtonClass(pageNum === page)}
                onClick={() => onPageChange(pageNum)}
                disabled={loading}
                aria-current={pageNum === page ? 'page' : undefined}
              >
                {pageNum}
              </button>
            )
          )}
        </div>

        <span className="sm:hidden text-sm text-gray-500 dark:text-gray-400">
          {page} / {totalPages}
        </span>

        <button
          className={navButtonClass}
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNextPage || loading}
          aria-label="Next page"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};
